// Procedural, seeded character portraits.
//
// A front-facing pixel-art bust drawn from a single seeded PRNG: the same
// (seed, faction) always yields the same face, so a portrait can be derived
// from a character's identity and re-rolled at will — unlimited variety, no art
// assets. This mirrors the map-tile renderer (js/modules/roomImageGen.js): a
// stable string->PRNG (makePRNG) plus a tiny rect/disc drawing API
// (js/modules/pixelDraw.js) painting a small logical canvas that gets
// nearest-neighbour upscaled to display size.
//
// Authored on a fixed 64x64 logical grid (AVATAR_BASE). All feature values are
// rolled up front, in a fixed order, into a `feat` object; drawing then reads
// that object. Rolling before drawing means a disabled config toggle removes
// only its own feature instead of shifting every later rng draw, so toggling a
// knob in avatar-lab.html changes one thing at a time.

import { makePRNG } from "../data/raidRegions.js";
import { makeG, disc, ri, hLine, vLine } from "./pixelDraw.js";

export const AVATAR_BASE = 64; // authored logical resolution
export const AVATAR_SIZE = 80; // default display px (the in-game avatar box)

// Faction palettes — the portrait's "theme", mirroring the pal: objects in
// RENDER_CONFIG.regions. Skin/hair/eye pools below are faction-independent.
export const AVATAR_PALETTES = {
  dominion: { bg1: "#2b3446", bg2: "#141a24", garb: "#3f4a5e", garbDark: "#28303f", accent: "#e0b45e", glow: "#7ec8ff" },
  veil:     { bg1: "#433a5a", bg2: "#14111d", garb: "#3a2c52", garbDark: "#221836", accent: "#b98cff", glow: "#66f0e0" },
  wilders:  { bg1: "#33301f", bg2: "#17160e", garb: "#4b3d29", garbDark: "#2e2519", accent: "#d8c9a0", glow: "#8fe37a" },
};

const SKIN_TONES = ["#f2cba3", "#e6b487", "#d69a6a", "#c07f4f", "#9c6238", "#7a4a2b", "#5a3720", "#f7d9bd"];
const HAIR_COLORS = ["#1c1a19", "#241f1b", "#3a2a1e", "#4a3527", "#5e3a22", "#6b4a2c", "#836a3f", "#b89b6a", "#8a8f98", "#2f2a26"];
// Uncommon / dyed hair colours — auburn, ginger, red, rose, purple, blue, teal,
// platinum, pink. Rare in random generation, always available in the creator.
const UNCOMMON_HAIR_COLORS = ["#7a3b2a", "#a64b2a", "#9c3a4a", "#b0555f", "#5a4a8c", "#3d6b8c", "#3f8a5f", "#d9d4cc", "#c77fbf"];
const EYE_COLORS  = ["#5b3a1e", "#3a2a18", "#3f5a3a", "#3a5570", "#5a5a62", "#2a2a2e"];
const EARRING_STYLES = ["stud", "hoop", "drop"];
// Gold, silver, then gemstone hues (ruby, sapphire, emerald, amethyst, rose).
const EARRING_COLORS = ["#e6c06a", "#cfd3db", "#c8384a", "#3f6fd0", "#3fa06a", "#9a6ad0", "#d65f95"];
const HAIR_STYLES = ["bald", "buzz", "crop", "swept", "long", "topknot", "curly"];
const FEMALE_HAIR_STYLES = ["long", "bob", "ponytail", "bun", "wavy", "curly"];

// Feature probabilities / knobs. Flat numeric values so avatar-lab.html can bind
// each to a single numeric input. glowEye is per-faction (veil skews eerie).
export const DEFAULT_AVATAR_CONFIG = {
  vignette: 0,
  hairChance: 0.9,
  facialHairChance: 0.35,
  scarChance: 0.16,
  visorChance: 0.01,
  eyepatchChance: 0.05,
  glassesChance: 0.15,
  markingsChance: 0.26,
  earringChance: 0.12,
  sigilChance: 0.05,
  glowEyeDominion: 0.05,
  glowEyeVeil: 0.55,
  glowEyeWilders: 0.15,
  veilHoodChance: 0.4, // how often a veil avatar wears the hood over the head
  femaleChance: 0.5, // fraction of avatars that are female-presenting
  wildHairChance: 0.08, // chance of an uncommon (dyed) hair colour
};

// Every editable feature, for building an avatar-creator UI. Types: bool; int
// (min/max); color (options = a suggested swatch pool); enum (options are plain
// values or [label, value] pairs). A field may carry get/set to map onto a
// different shape on the feature object — eyewear is stored as three booleans.
const ALL_HAIR_STYLES = [...new Set([...HAIR_STYLES, ...FEMALE_HAIR_STYLES])];
const ACC = "Accessories & markings";
// Capitalize an internal value string for display without changing the value.
const titleOpt = (v) => [v.charAt(0).toUpperCase() + v.slice(1), v];
export const FEATURE_SCHEMA = [
  { group: "Face", key: "gender", type: "enum", label: "Gender", options: [["Male", false], ["Female", true]],
    get: (f) => !!f.female, set: (f, v) => { f.female = v; } },
  { group: "Face", key: "skin", type: "color", label: "Skin", options: SKIN_TONES },
  { group: "Face", key: "faceW", type: "int", label: "Width", min: 14, max: 20 },
  { group: "Face", key: "faceH", type: "int", label: "Height", min: 34, max: 46 },
  { group: "Face", key: "jaw", type: "enum", label: "Jaw", options: [["Round", 0], ["Square", 1], ["Narrow", 2]] },
  { group: "Face", key: "shadeSide", type: "enum", label: "Light from", options: [["Left", 1], ["Right", -1]] },
  { group: "Face", key: "mouthW", type: "int", label: "Mouth width", min: 3, max: 10 },
  { group: "Face", key: "mouthCurve", type: "enum", label: "Mouth", options: [["Frown", -1], ["Neutral", 0], ["Smile", 1]] },

  { group: "Hair", key: "hairStyle", type: "enum", label: "Style", options: ALL_HAIR_STYLES.map(titleOpt) },
  { group: "Hair", key: "hairColor", type: "color", label: "Color", options: [...HAIR_COLORS, ...UNCOMMON_HAIR_COLORS] },
  { group: "Hair", key: "hairline", type: "int", label: "Hairline", min: 4, max: 14 },
  { group: "Hair", key: "facialHair", type: "enum", label: "Facial hair", options: [["None", null], ["Stubble", "stubble"], ["Mustache", "mustache"], ["Beard", "beard"], ["Goatee", "goatee"]]},

  { group: "Eyes", key: "eyeColor", type: "color", label: "Color", options: EYE_COLORS },
  { group: "Eyes", key: "eyeGap", type: "int", label: "Spacing", min: 10, max: 18 },
  { group: "Eyes", key: "eyeYoff", type: "int", label: "Height", min: -2, max: 2 },
  { group: "Eyes", key: "gaze", type: "int", label: "Gaze", min: -1, max: 1 },
  { group: "Eyes", key: "browRaise", type: "int", label: "Brow raise", min: 0, max: 3 },
  { group: "Eyes", key: "browThick", type: "int", label: "Brow thickness", min: 1, max: 2 },
  { group: "Eyes", key: "glowEye", type: "bool", label: "Glow eyes" },

  { group: ACC, key: "eyewear", type: "enum", label: "Eyewear",
    options: [["None", "none"], ["Visor", "visor"], ["Glasses", "glasses"], ["Eyepatch", "eyepatch"]],
    get: (f) => (f.visor ? "visor" : f.glasses ? "glasses" : f.eyepatch ? "eyepatch" : "none"),
    set: (f, v) => { f.visor = v === "visor"; f.glasses = v === "glasses"; f.eyepatch = v === "eyepatch"; } },
  { group: ACC, key: "glassesStyle", type: "enum", label: "Glasses style", options: [["Clear", "clear"], ["Sunglasses", "sun"], ["Aviators", "aviator"]] },
  { group: ACC, key: "earrings", type: "enum", label: "Earrings",
    options: [["None", "none"], ["One", "1"], ["Two", "2"]],
    get: (f) => (f.earring ? (f.earringBoth ? "2" : "1") : "none"),
    set: (f, v) => { f.earring = v !== "none"; f.earringBoth = v === "2"; } },
  { group: ACC, key: "earringStyle", type: "enum", label: "Earring style", options: EARRING_STYLES.map(titleOpt) },
  { group: ACC, key: "earringColor", type: "color", label: "Earring color", options: EARRING_COLORS },
  { group: ACC, key: "scar", type: "bool", label: "Scar" },
  { group: ACC, key: "scarSide", type: "enum", label: "Scar side", options: [["Left", -1], ["Right", 1]] },
  { group: ACC, key: "markings", type: "bool", label: "Markings" },
  { group: ACC, key: "markStyle", type: "enum", label: "Marking style", options: [["Eye Stripes", 0], ["Cheek Dashes", 1], ["Forehead Mark", 2]] },
  { group: ACC, key: "sigil", type: "bool", label: "Faction sigil" },
  { group: ACC, key: "hood", type: "bool", label: "Hood", factions: ["veil"] },
];

// ---- colour helpers -------------------------------------------------------
const clamp255 = (n) => (n < 0 ? 0 : n > 255 ? 255 : n | 0);
function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const rgbToHex = (r, g, b) =>
  "#" + [r, g, b].map((v) => clamp255(v).toString(16).padStart(2, "0")).join("");
// Multiply toward black (f<1) or white-ish (f>1) for cheap shading/highlights.
function shade(hex, f) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * f, g * f, b * f);
}
function lerpHex(a, b, t) {
  const [r1, g1, b1] = hexToRgb(a), [r2, g2, b2] = hexToRgb(b);
  return rgbToHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}
function pick(rng, arr) {
  return arr[Math.floor(rng() * arr.length)];
}
// Deterministic 0..1 value hash for position-based texture (no rng stream, so
// it never perturbs feature rolls).
function hash2(x, y) {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

// ---- feature roll ---------------------------------------------------------
// Consume the rng in a FIXED order regardless of config, so toggles are local.
function rollFeatures(rng, faction, cfg) {
  const f = {};
  f.skin = pick(rng, SKIN_TONES);
  f.female = rng() < cfg.femaleChance;
  f.faceW = ri(rng, 16, 18); // half-width at the cheekbones (widest)
  f.faceH = ri(rng, 38, 42); // crown-to-chin height
  f.jaw = ri(rng, 0, 2); // 0 round, 1 square, 2 narrow
  f.shadeSide = rng() < 0.5 ? -1 : 1;
  // Both the presence check and the style pick always consume the rng (fixed
  // stream). Females always have hair and draw from a female style pool.
  const hairPresent = rng() < cfg.hairChance;
  const styleRoll = pick(rng, f.female ? FEMALE_HAIR_STYLES : HAIR_STYLES);
  f.hairStyle = f.female || hairPresent ? styleRoll : "bald";
  const wildHair = rng() < cfg.wildHairChance; // rolled + both pools picked always (fixed stream)
  const normalHair = pick(rng, HAIR_COLORS), wildHairCol = pick(rng, UNCOMMON_HAIR_COLORS);
  f.hairColor = wildHair ? wildHairCol : normalHair;
  f.hairline = ri(rng, 7, 11); // px of forehead the hair leaves bare
  f.eyeColor = pick(rng, EYE_COLORS);
  f.eyeYoff = ri(rng, -1, 1);
  f.eyeGap = ri(rng, 14, 16);
  f.gaze = ri(rng, -1, 1);
  f.browRaise = ri(rng, 0, 2);
  f.browThick = rng() < 0.4 ? 2 : 1;
  f.mouthW = ri(rng, 4, 8);
  f.mouthCurve = ri(rng, -1, 1); // -1 frown, 0 neutral, 1 smile
  const fhPresent = rng() < cfg.facialHairChance; // rolled + picked always (fixed stream)
  const fhStyle = pick(rng, ["stubble", "mustache", "beard", "goatee"]);
  f.facialHair = !f.female && fhPresent ? fhStyle : null; // females: no facial hair
  f.scar = rng() < cfg.scarChance;
  f.scarSide = rng() < 0.5 ? -1 : 1;
  f.visor = rng() < cfg.visorChance;
  f.eyepatch = !f.visor && rng() < cfg.eyepatchChance;
  const glassesRoll = rng() < cfg.glassesChance; // rolled always so the stream stays fixed
  f.glasses = !f.visor && !f.eyepatch && glassesRoll; // one eyewear accessory at a time
  const gsRoll = rng();
  f.glassesStyle = gsRoll < 0.5 ? "clear" : gsRoll < 0.8 ? "sun" : "aviator"; // clear / sunglasses / aviator
  f.markings = rng() < cfg.markingsChance;
  f.markStyle = ri(rng, 0, 2);
  f.earring = rng() < cfg.earringChance * (f.female ? 2.5 : 1); // more common on females
  f.earringStyle = pick(rng, EARRING_STYLES);
  f.earringBoth = rng() < (f.female ? 0.6 : 0.2); // one or two earrings
  f.earringColor = pick(rng, EARRING_COLORS);
  f.sigil = rng() < cfg.sigilChance;
  const glowChance = { dominion: cfg.glowEyeDominion, veil: cfg.glowEyeVeil, wilders: cfg.glowEyeWilders }[faction] ?? 0.1;
  f.glowEye = rng() < glowChance;
  const hoodRoll = rng() < cfg.veilHoodChance; // rolled always so the stream stays fixed
  f.hood = faction === "veil" && hoodRoll; // only veil wears the head covering, and only sometimes
  return f;
}

// ---- layers ---------------------------------------------------------------
function drawBackground(g, pal, cfg) {
  // Vertical gradient bands (bg1 top -> bg2 bottom).
  for (let y = 0; y < AVATAR_BASE; y++) {
    g.r(0, y, AVATAR_BASE, 1, lerpHex(pal.bg1, pal.bg2, y / AVATAR_BASE));
  }
  // Corner vignette.
  if (cfg.vignette > 0) {
    const v = shade(pal.bg2, 0.5);
    for (const [cx, cy] of [[0, 0], [AVATAR_BASE, 0], [0, AVATAR_BASE], [AVATAR_BASE, AVATAR_BASE]]) {
      g.a(cfg.vignette, () => disc(g, cx, cy, 22, v));
    }
  }
}

// A parametric skull silhouette shared by every layer: a rounded crown, roughly
// straight temples/cheeks (widest), then a jaw that tapers to the chin.
// faceHalf(y) returns the head's half-width at row y, so skin, hair, beard and
// cowl all trace the same outline. Authored to fill most of the 64px frame and
// crop the shoulders at the edges, matching the reference sprite busts.
function buildFaceHalf(headTop, headH, maxHalf, chinHalf, jaw) {
  return (y) => {
    const t = (y - headTop) / headH;
    if (t < 0 || t > 1) return 0;
    if (t < 0.3) {
      const tt = t / 0.3; // crown: elliptical dome
      return maxHalf * Math.sqrt(Math.max(0, 1 - (1 - tt) * (1 - tt)));
    }
    if (t < 0.58) return maxHalf; // temples + cheekbones (widest)
    const tt = (t - 0.58) / 0.42; // jaw -> chin
    if (jaw === 1) {
      // square: hold width, then clip in near the bottom
      return tt < 0.72 ? maxHalf : Math.max(chinHalf, maxHalf - (maxHalf - chinHalf) * ((tt - 0.72) / 0.28));
    }
    return maxHalf + (chinHalf - maxHalf) * (tt * tt); // round/narrow: eased curve
  };
}

function makeGeom(f) {
  const cx = 32, headTop = 5, headH = f.faceH;
  // Females get a slightly narrower face and always use a soft, rounded jaw
  // profile (never the angular square). The jaw option still applies — it varies
  // the chin width: round (soft), square (fuller jaw), narrow (pointed / heart).
  const maxHalf = f.female ? f.faceW - 1 : f.faceW;
  const jaw = f.female ? 0 : f.jaw;
  const chinY = headTop + headH;
  const chinHalf =
    f.female ? Math.round(maxHalf * (f.jaw === 1 ? 0.56 : f.jaw === 2 ? 0.36 : 0.46))
    : jaw === 2 ? Math.round(maxHalf * 0.42) // narrow
    : jaw === 1 ? Math.round(maxHalf * 0.62) // square
    : Math.round(maxHalf * 0.52); // round
  const faceHalf = buildFaceHalf(headTop, headH, maxHalf, chinHalf, jaw);
  // A 1px-inflated silhouette used to lay down a dark self-outline before skin.
  const outlineHalf = buildFaceHalf(headTop - 1, headH + 2, maxHalf + 1, chinHalf + 1, jaw);
  // Eyeline at ~half the head, with wider gaps down to the nose base and mouth so
  // the features fill the face instead of bunching in the lower-middle.
  const eyeY = Math.round(headTop + headH * 0.5) + f.eyeYoff;
  return {
    cx, headTop, headH, maxHalf, chinHalf, chinY, faceHalf, outlineHalf, jaw,
    eyeY, noseY: eyeY + 7, mouthY: eyeY + 14,
    hairlineY: headTop + f.hairline,
    neckHalf: Math.max(5, Math.round(chinHalf * 0.8)),
    shoulderTop: chinY + 5, // gap below the chin so the neck is visible before the collar
  };
}

// Half-width of the veil hood — the head outline plus a margin, holding wide all
// the way down to the shoulders.
function makeHoodHalf(geom) {
  const top = geom.headTop - 4, span = geom.chinY + 8 - top, wide = geom.maxHalf + 5;
  return (y) => {
    const t = (y - top) / span;
    if (t < 0 || t > 1) return 0;
    if (t < 0.34) {
      const tt = t / 0.34;
      return wide * Math.sqrt(Math.max(0, 1 - (1 - tt) * (1 - tt)));
    }
    return wide;
  };
}

function drawBody(g, geom, rng, f, pal, faction) {
  const { cx, shoulderTop } = geom;
  const gd = pal.garbDark, gl = shade(pal.garb, 1.16);
  const fold = shade(pal.garb, 0.68), foldLit = shade(pal.garb, 1.24);
  const inner = geom.neckHalf + 3, full = 40, drop = 0.45;
  // Sloped shoulders descending from the neck. topAt is the garment's top edge
  // per column, halfAt its half-width per row. (A fast sqrt curve used to flare
  // the shoulder tops up too high, near the jaw.) Drawing by column makes the
  // sloped top edge — and the detail that rides it — fall out for free.
  const topAt = (x) => shoulderTop + Math.round(drop * Math.max(0, Math.abs(x - cx) - inner));
  const halfAt = (y) => Math.min(full, inner + (y - shoulderTop) / drop);
  const edge = (fn) => { for (let x = cx - full; x <= cx + full; x++) { const top = topAt(x); if (top < AVATAR_BASE) fn(x, top, Math.abs(x - cx)); } };

  // Fill by columns from the sloped top edge to the bottom. Overdraw one row past
  // the bottom edge so a breathing lift never exposes a gap there (clipped away).
  edge((x, top) => g.r(x, top, 1, AVATAR_BASE - top + 1, pal.garb));
  // Collar rising up either side of the neck.
  g.r(cx - geom.neckHalf - 3, shoulderTop - 4, 3, 6, pal.garb);
  g.r(cx + geom.neckHalf, shoulderTop - 4, 3, 6, pal.garb);

  // Shading: highlight riding the shoulder edge, a centre-chest shadow, and
  // darker lower-outer corners, so the garment reads rounded not slab-flat.
  g.a(0.4, () => edge((x, top, dx) => { if (dx >= inner) g.r(x, top, 1, 2, gl); }));
  g.a(0.22, () => { for (let y = shoulderTop + 4; y < AVATAR_BASE; y++) g.r(cx - 3, y, 6, 1, gd); });
  g.a(0.35, () => { for (let y = shoulderTop + 8; y < AVATAR_BASE; y++) { const h = halfAt(y); g.r(cx - h, y, 5, 1, gd); g.r(cx + h - 5, y, 5, 1, gd); } });

  // Fabric weave: a deterministic speckle so cloth reads textured, not plastic —
  // a dark grain plus a sparser lit fleck.
  g.a(0.18, () => edge((x, top) => { for (let y = top; y < AVATAR_BASE; y++) if (hash2(x * 1.3, y * 2.1) > 0.86) g.r(x, y, 1, 1, gd); }));
  g.a(0.12, () => edge((x, top) => { for (let y = top; y < AVATAR_BASE; y++) if (hash2(x * 2.7, y * 1.1) < 0.07) g.r(x, y, 1, 1, gl); }));
  // Wrinkle creases fanning down-outward from the collar (short dark diagonals),
  // the readable, hand-placed counterpart to the random weave.
  g.a(0.3, () => {
    for (const sgn of [-1, 1]) for (const k of [0, 1, 2]) {
      let wx = cx + sgn * (inner + 1), wy = shoulderTop + 3 + k * 4;
      for (let j = 0; j < 6 - k; j++) { if (wy >= topAt(wx) && wy < AVATAR_BASE) g.r(wx, wy, 1, 1, fold); wx += sgn; wy += 1; }
    }
  });

  // Stacked fabric folds (dark crease + lit edge above), clamped inside the body.
  const folds = (count, startY, spacing, w0, dw) => {
    for (let i = 0; i < count; i++) {
      const y = startY + i * spacing, w = Math.min(halfAt(y) - 1, w0 + i * dw);
      for (let x = cx - w; x <= cx + w; x++) { const t = (x - cx) / w; g.r(x, y + Math.round(2.2 * t * t), 1, 1, fold); }
      g.a(0.45, () => { for (let x = cx - w; x <= cx + w; x++) { const t = (x - cx) / w; g.r(x, y + Math.round(2.2 * t * t) - 1, 1, 1, foldLit); } });
    }
  };

  if (faction === "dominion") {
    // Stiff high collar, a zipper seam down the chest, gold neckline trim + stud.
    folds(1, shoulderTop + 5, 0, geom.neckHalf + 7, 0);
    g.a(0.7, () => g.r(cx - 1, shoulderTop + 2, 2, AVATAR_BASE - shoulderTop, gd)); // seam channel
    for (let y = shoulderTop + 3; y < AVATAR_BASE; y += 2) g.r(cx, y, 1, 1, foldLit); // zipper teeth
    hLine(g, cx - inner, shoulderTop, inner * 2, pal.accent); // collar trim across the neckline
    g.r(cx - 1, shoulderTop + 5, 3, 3, pal.accent); // stud
  } else if (faction === "veil") {
    // Layered scarf/cowl wraps — the signature veil look — plus one faint
    // asymmetric accent thread.
    folds(3, shoulderTop + 3, 3, geom.neckHalf + 5, 5);
    g.a(0.25, () => { for (let y = shoulderTop + 4; y < AVATAR_BASE; y++) g.r(cx - 9 + (y - shoulderTop), y, 1, 1, pal.accent); });
  } else {
    // Wilders: ragged fur riding the sloped top edge, a diagonal leather strap,
    // one heavy fold, and a bone pendant.
    edge((x, top, dx) => { if (dx < inner - 2) return; const jag = ri(rng, 0, 2); g.r(x, top + jag - 1, 1, 2, ri(rng, 0, 1) ? gd : gl); });
    folds(1, shoulderTop + 8, 0, geom.neckHalf + 8, 0);
    for (let i = 0; i < 16; i++) { const x = cx - 10 + i, y = shoulderTop + 5 + i; if (y > topAt(x)) g.r(x, y, 2, 1, shade(pal.garb, 0.58)); } // strap
    vLine(g, cx, shoulderTop + 5, 4, pal.accent);
    g.r(cx - 1, shoulderTop + 8, 3, 2, pal.accent); // bone pendant
  }
}

// Veil cowl framing — a hood BEHIND the head; the front rim is redrawn after
// hair so it sits over the crown and closes around the face.
function cowlBack(g, geom, pal) {
  const hood = makeHoodHalf(geom);
  for (let y = geom.headTop - 4; y <= geom.shoulderTop + 2; y++) {
    const h = Math.round(hood(y));
    if (h > 0) g.r(geom.cx - h, y, 2 * h, 1, pal.garbDark);
  }
}
function cowlFront(g, geom, pal) {
  const { cx } = geom;
  const hood = makeHoodHalf(geom);
  const shadow = shade(pal.garbDark, 0.75);
  for (let y = geom.headTop - 4; y <= geom.chinY; y++) {
    const hh = Math.round(hood(y));
    if (hh <= 0) continue;
    if (y < geom.hairlineY) {
      g.r(cx - hh, y, 2 * hh, 1, pal.garbDark); // hood over the crown
    } else {
      const fh = Math.round(geom.faceHalf(y)) + 1; // frame the sides, leave the face open
      g.r(cx - hh, y, hh - fh, 1, pal.garbDark);
      g.r(cx + fh, y, hh - fh, 1, pal.garbDark);
    }
  }
  g.a(0.5, () => g.r(cx - geom.maxHalf, geom.hairlineY, geom.maxHalf * 2, 3, shadow)); // brow shadow under the hood
}

function drawHead(g, geom, f) {
  const { cx, headTop, chinY, faceHalf, outlineHalf } = geom;
  const skin = f.skin;
  const base = skin;
  const shadow = shade(skin, 0.74); // shadow plane
  const deep = shade(skin, 0.56); // core shadows (sockets, under-jaw)
  const lit = shade(skin, 1.12); // lit plane
  const hi = shade(skin, 1.20); // highlight points (cheekbone, chin, brow ridge)
  const line = shade(skin, 0.42); // self-outline
  const s = f.shadeSide; // face lit from -s, shadowed toward +s
  const eyeY = geom.eyeY;

  const er = Math.round(faceHalf(eyeY + 1)); // cheekbone half-width, reused for shading below

  // Neck dropping from the chin into the collar, in shadow.
  const nh = geom.neckHalf;
  g.r(cx - nh - 1, chinY - 3, 1, geom.shoulderTop + 2 - (chinY - 3), line); // neck outline
  g.r(cx + nh, chinY - 3, 1, geom.shoulderTop + 2 - (chinY - 3), line);
  g.r(cx - nh, chinY - 3, nh * 2, geom.shoulderTop + 2 - (chinY - 3), shadow);
  g.r(cx - nh, chinY - 3, nh * 2, 2, deep); // under-jaw shadow

  // 1) Dark self-outline (inflated silhouette).
  for (let y = headTop - 1; y <= chinY + 1; y++) {
    const h = Math.round(outlineHalf(y));
    if (h > 0) g.r(cx - h, y, 2 * h, 1, line);
  }
  // 2) Base skin.
  for (let y = headTop; y <= chinY; y++) {
    const h = Math.round(faceHalf(y));
    if (h > 0) g.r(cx - h, y, 2 * h, 1, base);
  }
  // 3) Shadow plane: a soft band along the shadow-side edge only (not half the
  // face) — the outer third, so most of the face stays base tone.
  for (let y = headTop + 1; y <= chinY; y++) {
    const h = Math.round(faceHalf(y));
    if (h <= 1) continue;
    const w = Math.max(2, Math.round(h * 0.4));
    if (s > 0) g.r(cx + (h - w), y, w, 1, shadow);
    else g.r(cx - h, y, w, 1, shadow);
  }
  // 4) Lit plane: a gentle highlight down the lit cheek/temple + nose bridge.
  g.a(0.5, () => {
    for (let y = geom.hairlineY + 1; y < eyeY; y++) {
      const h = Math.round(faceHalf(y));
      g.r(s > 0 ? cx - h + 1 : cx + Math.round(h * 0.35), y, Math.round(h * 0.4), 1, lit);
    }
    g.r(s > 0 ? cx - er + 1 : cx + Math.round(er * 0.35), eyeY + 4, 3, 2, lit); // cheekbone
  });
  // 5) Under-cheek jaw shadow (eye sockets are shaded per-eye in drawFace).
  g.a(0.16, () => { for (let y = geom.mouthY; y <= chinY - 2; y++) { const h = Math.round(faceHalf(y)); if (h > 2) g.r(cx - h + 1, y, 2 * h - 2, 1, shadow); } });
  // 6) Ears: a vertical oval protruding from each side (brow-to-nose level). An
  // elliptical protrusion gives the rounded oval outline, an outer outline is the
  // helix, and an inner shadow curve is the concha bowl — so they read as ears,
  // not bumps. Hair/sideburns cover the tops.
  const earTop = eyeY - 1, earBot = geom.noseY;
  const earCy = (earTop + earBot) / 2, earHh = (earBot - earTop) / 2 + 0.6;
  for (const sgn of [-1, 1]) {
    const earSkin = sgn === s ? shadow : base;
    for (let y = earTop; y <= earBot; y++) {
      const edge = Math.round(faceHalf(y));
      const nrm = (y - earCy) / earHh; // -1 top .. +1 bottom
      const prot = Math.round(3.2 * Math.sqrt(Math.max(0, 1 - nrm * nrm))); // ellipse -> rounded oval
      if (prot <= 0) continue;
      for (let k = 0; k < prot; k++) g.r(cx + sgn * (edge + k), y, 1, 1, earSkin);
      g.r(cx + sgn * (edge + prot), y, 1, 1, line); // outer helix outline
      g.r(cx + sgn * (edge - 1), y, 1, 1, deep); // behind-ear (attach) shadow
      if (Math.abs(nrm) < 0.6 && prot >= 3) g.r(cx + sgn * (edge + 1), y, 1, 1, deep); // concha bowl
    }
  }
  // 7) Facial contours (cheekbones, cheek hollows, brow ridge, chin, temples).
  drawContours(g, geom, f, { shadow, deep, lit, hi });
}

// Local contour shading that gives the face form instead of a flat mask. All
// shapes are anchored to the same geometry and lit from -s (the shadow side is
// +s), so every portrait reads with one coherent light. Kept low-alpha and small
// so it sculpts without muddying.
function drawContours(g, geom, f, { shadow, deep, lit, hi }) {
  const { cx, eyeY, noseY, mouthY, chinY, hairlineY, faceHalf } = geom;
  const s = f.shadeSide;
  const mw = f.mouthW;

  // Brow-bone catch-light just above the brows (stronger on the lit side).
  g.a(0.45, () => {
    const w = Math.round(faceHalf(eyeY) * 0.55);
    g.r(s > 0 ? cx - w - 1 : cx + 2, eyeY - 4, w, 1, lit);
  });

  // Cheekbone highlight + a cheek hollow curving down toward the mouth corner.
  // The hollow is the main "this is a face, not a ball" cue; deeper on the shadow
  // side, hinted on the lit side.
  for (const sgn of [-1, 1]) {
    const cheekY = eyeY + 4;
    const cbx = cx + sgn * Math.round(faceHalf(cheekY) * 0.6);
    g.a(sgn === -s ? 0.55 : 0.3, () => g.r(cbx - 1, cheekY, 3, 2, hi)); // cheekbone
    let hx = cx + sgn * Math.round(faceHalf(cheekY + 2) * 0.74);
    g.a(sgn === s ? 0.32 : 0.16, () => {
      for (let y = cheekY + 2; y < mouthY; y++) {
        g.r(hx - (sgn < 0 ? 1 : 0), y, 2, 1, shadow);
        hx -= sgn; // curve inward toward the mouth
      }
    });
  }

  // Temple hollow on the lit side (the shadow side is already covered by the
  // shadow plane) so the forehead reads rounded, not flat.
  g.a(0.2, () => {
    for (let y = hairlineY + 1; y < eyeY - 2; y++) {
      const h = Math.round(faceHalf(y));
      g.r(s > 0 ? cx - h : cx + h - 2, y, 2, 1, shadow);
    }
  });

  // Philtrum (under-nose) soft shadow on the shadow side.
  g.a(0.22, () => g.r(cx + (s > 0 ? 0 : -2), noseY + 5, 2, 1, shadow));

  // Under-lip crease + a lit chin ball below it.
  g.a(0.3, () => g.r(cx - (mw >> 1) + 1, mouthY + 2, mw - 2, 1, deep));
  g.a(0.5, () => g.r(cx - 2, chinY - 2, 5, 2, hi));
}

function drawHair(g, geom, f, anim = null) {
  const { cx, headTop, chinY, faceHalf, hairlineY, maxHalf } = geom;
  if (f.hairStyle === "bald") {
    g.a(0.1, () => g.r(cx - 4, headTop + 4, 7, 3, shade(f.skin, 1.14))); // scalp sheen
    return;
  }
  const col = f.hairColor, dark = shade(col, 0.66), line = shade(col, 0.45), lit = shade(col, 1.2);
  const s = f.shadeSide;
  // Wind rustle (idle animation): the loose hair leans horizontally, growing from
  // the roots (hairline) toward the tips, so it flutters rather than sliding as a
  // rigid block. sway(y) is 0 without a gust, so static output stays unchanged.
  const wind = anim?.wind || 0;
  const sway = wind === 0 ? () => 0
    : (y) => Math.round(wind * 2 * Math.min(1, Math.max(0, (y - hairlineY) / 26)));
  // Darken a strand that falls on the shadow side (sign === s), so a drape over
  // the cheek carries the same side shadow as the face beneath it.
  const sideCol = (sign, base) => shade(base, sign === s ? 0.72 : 1);
  const curly = f.hairStyle === "curly";
  // Hair is the head silhouette lifted up by `lift` and inflated by 1px, so the
  // crown is one smooth continuous dome (no pinch/gap at the apex) that sits just
  // outside the skin at the hairline.
  const lift = curly ? 3 : 2;
  const hairTop = headTop - lift;
  const hairProfile = buildFaceHalf(hairTop, geom.headH, maxHalf + 1, geom.chinHalf, geom.jaw);
  const hairHalf = (y) => hairProfile(y) + (curly && hash2(y, 7) > 0.55 ? 1 : 0);
  // Solid crown: outline, fill, then a shadow-side plane like the skin.
  for (let y = hairTop; y <= hairlineY; y++) {
    const h = Math.round(hairHalf(y));
    if (h <= 0) continue;
    g.r(cx - h - 1, y, 2 * h + 2, 1, line);
    g.r(cx - h, y, 2 * h, 1, col);
    const w = Math.max(2, Math.round(h * 0.4)); // shadow side
    if (s > 0) g.r(cx + h - w, y, w, 1, dark);
    else g.r(cx - h, y, w, 1, dark);
  }
  // Soft hairline shadow where the hair meets the forehead (one row, so it reads
  // as a hairline, not a hat brim).
  {
    const h = Math.round(hairHalf(hairlineY));
    if (h > 0) g.a(0.55, () => g.r(cx - h, hairlineY, 2 * h, 1, dark));
  }
  // Small highlight patch on the lit side of the crown.
  g.a(0.5, () => {
    for (let y = hairTop + 1; y < headTop + 4; y++) {
      const h = Math.round(hairHalf(y));
      g.r(s > 0 ? cx - Math.round(h * 0.75) : cx + Math.round(h * 0.35), y, Math.max(2, Math.round(h * 0.35)), 1, lit);
    }
  });
  // Restrained texture: a few dark strand clumps only (no scattered light pixels
  // — those read as noise). Curly gets a regular clump pattern.
  if (curly) {
    for (let y = hairTop + 1; y < hairlineY; y += 2) {
      const h = Math.round(hairHalf(Math.max(y, headTop)));
      for (let x = cx - h + 1; x < cx + h - 1; x += 3) {
        if (hash2(x, y) > 0.5) g.r(x + (hash2(x, y) > 0.75 ? 1 : 0), y, 2, 1, dark);
      }
    }
  } else {
    for (let y = hairTop + 2; y < hairlineY - 1; y += 3) {
      const h = Math.round(hairHalf(Math.max(y, headTop)));
      g.a(0.6, () => g.r(cx - Math.round(h * 0.5), y, Math.round(h), 1, dark)); // soft strand line
    }
  }
  // Frame the face: hair descends the temples down to cheek level (drapes lower
  // for long hair), so it wraps the face instead of sitting as a flat cap.
  const bobBot = Math.round((geom.mouthY + chinY) / 2); // bob ends midway between mouth and chin
  const longDrape = f.hairStyle === "long" || f.hairStyle === "wavy";
  const coversEars = longDrape || f.hairStyle === "bob"; // longer styles reach out over the ears
  // The ear bulge eases in from the hairline and back out around the mouth, so
  // the widening is very gradual (peak ~3px near the ear centre).
  const earTop = hairlineY-2, earBot = geom.mouthY+2, earSpan = earBot - earTop;
  const frameBottom = f.hairStyle === "bob" ? bobBot : longDrape ? chinY - 2 : f.hairStyle === "buzz" ? hairlineY + 3 : geom.eyeY + 2;
  const sideW = f.female ? 4 : curly ? 4 : 3;
  for (let y = hairlineY - 1; y <= frameBottom; y++) {
    const h = Math.round(faceHalf(Math.min(y, chinY)) ?? 0) + 1;
    // A smooth bulge out over the ears — ramps gradually out and back in.
    const ext = coversEars && y >= earTop && y <= earBot
      ? Math.round(3.3 * Math.sin(((y - earTop) / earSpan) * Math.PI))
      : 0;
    const sw = sway(y);
    g.r(cx - h - 1 - ext + sw, y, 1, 1, sideCol(-1, line));
    g.r(cx - h - ext + sw, y, sideW + ext, 1, sideCol(-1, hash2(y, 1) > 0.8 ? dark : col));
    g.r(cx + h - sideW + sw, y, sideW + ext, 1, sideCol(1, hash2(y, 2) > 0.8 ? dark : col));
    g.r(cx + h + ext + sw, y, 1, 1, sideCol(1, line));
  }
  // Style-specific extras.
  switch (f.hairStyle) {
    case "swept": { // a fringe sweeping across the forehead toward the lit side
      const dir = -s;
      for (let i = 0; i < maxHalf + 2; i++) {
        const x = cx - (maxHalf * dir) + i * dir;
        g.r(x, hairlineY - 2 + ((i / 4) | 0), 2, 2, i % 3 ? col : dark);
      }
      break;
    }
    case "long":
    case "wavy": { // fuller drape past the shoulders; wavy undulates at the outer edge
      const w = f.female ? 4 : 3, wavy = f.hairStyle === "wavy";
      for (let y = geom.eyeY - 1; y < chinY + 16; y++) {
        const h = Math.round(faceHalf(Math.min(y, chinY))) + 1;
        const wob = wavy ? Math.max(0, Math.round(1.6 * Math.sin(y * 0.55))) : 0;
        const c = hash2(y, 3) > 0.82 ? dark : col, sw = sway(y);
        g.r(cx - h - 1 - wob + sw, y, w, 1, sideCol(-1, c));
        g.r(cx + h - w + 1 + wob + sw, y, w, 1, sideCol(1, c));
      }
      break;
    }
    case "bob": // ends midway between mouth and chin, curling inward at the bottom
      for (let y = geom.eyeY - 2; y <= bobBot; y++) {
        const h = Math.round(faceHalf(Math.min(y, chinY))) + 1;
        const inCurl = Math.max(0, y - (bobBot - 3));
        const c = hash2(y, 3) > 0.85 ? dark : col, sw = sway(y);
        g.r(cx - h - 1 + inCurl + sw, y, 4, 1, sideCol(-1, c));
        g.r(cx + h - 3 - inCurl + sw, y, 4, 1, sideCol(1, c));
      }
      break;
    case "ponytail": { // a tie plus a tail hanging behind the shadow-side shoulder
      const tx = cx + s * (maxHalf + 2);
      g.r(tx - 1, geom.eyeY - 2, 3, 2, dark); // hair tie
      for (let y = geom.eyeY; y < chinY + 12; y++) {
        const wob = Math.round(1.4 * Math.sin(y * 0.5));
        g.r(tx - 1 + wob + sway(y), y, 3, 1, sideCol(s, hash2(y, 5) > 0.75 ? dark : col));
      }
      break;
    }
    case "bun": // hair gathered into a bun on top
      disc(g, cx, hairTop - 2, 5, line);
      disc(g, cx, hairTop - 2, 4, col);
      g.a(0.5, () => disc(g, cx - 1, hairTop - 3, 2, lit));
      break;
    case "topknot":
      g.r(cx - 2, hairTop - 4, 4, 5, col); // knot stub
      disc(g, cx, hairTop - 1, 3, col);
      g.a(0.6, () => disc(g, cx - 1, hairTop - 2, 1, lit));
      break;
  }
}

function drawFace(g, geom, f, pal, anim = null) {
  const { cx, eyeY: ey, noseY, mouthY: my } = geom;
  const [lx, rx] = [cx - (f.eyeGap >> 1), cx + (f.eyeGap >> 1)];
  const browCol = shade(f.hairStyle === "bald" ? "#5a4634" : f.hairColor, 0.8);
  const iris = f.glowEye ? pal.glow : f.eyeColor;
  const socket = shade(f.skin, 0.82);
  // Idle-animation deltas (all no-ops when anim is null, so static renders and
  // the unit tests are unchanged): a gaze saccade, a brow flick, a blink, and a
  // multiplier on the glow-eye pulse.
  const gaze = Math.max(-2, Math.min(2, f.gaze + (anim?.gaze || 0)));
  const browRaise = f.browRaise + (anim?.brow || 0);
  const shut = (anim?.blink || 0) > 0.6; // eyes closed this frame
  const glowMul = anim?.glow ?? 1;

  const eye = (ex, dir) => {
    g.a(0.55, () => g.r(ex - 2, ey - 1, 5, 1, socket)); // socket shading above the eye
    if (shut) {
      // Closed lid: a soft skin crease where the eye was, no iris/catch-light.
      g.r(ex - 2, ey, 4, 1, shade(f.skin, 0.9));
      g.r(ex - 2, ey + 1, 4, 1, shade(f.skin, 0.7));
    } else {
      g.r(ex - 2, ey, 4, 1, "#e9e6de"); // small almond sclera
      g.r(ex - 2, ey + 1, 4, 1, shade("#e9e6de", 0.8)); // lower lid line
      g.r(ex - 1 + gaze, ey, 2, 2, iris); // iris fills most of the eye
      g.r(ex + gaze, ey, 1, 1, "#141317"); // pupil
      g.r(ex - 2, ey, 1, 1, "#2a2622"); // inner/outer canthus
      g.r(ex + 1, ey, 1, 1, "#2a2622");
      // Catch-light: a single lit iris pixel, drawn last so the canthus can't
      // paint over it, and on the same side of both eyes so they match.
      g.r(ex - 1 + gaze, ey, 1, 1, "#ffffff");
      if (f.glowEye) g.a(0.22 * glowMul, () => disc(g, ex + gaze, ey + 1, 2, pal.glow));
    }
    // Brow: a short stroke angled down toward the nose. Females get a thinner,
    // slightly higher, arched brow plus a lash at the outer corner.
    if (f.female) {
      const bw = 5, by = ey - 4 - browRaise;
      hLine(g, ex - 2, by, bw, browCol);
      g.r(ex - 2 + (dir > 0 ? bw - 1 : 0), by - 1, 1, 1, browCol); // outer tip arches up
      g.r(ex + (dir > 0 ? 2 : -2), ey - 1, 1, 1, "#241f1b"); // eyelash
    } else {
      const bw = 6, by = ey - 3 - browRaise;
      hLine(g, ex - 3, by, bw, browCol);
      g.r(ex - 3 + (dir > 0 ? bw - 1 : 0), by + 1, 1, 1, browCol); // outer end drops
      if (f.browThick === 2) hLine(g, ex - 3, by + 1, bw, browCol);
    }
  };
  eye(lx, -1);
  eye(rx, 1);

  // Nose: a shaded ridge on the shadow side, a lit bridge on the lit side, and a
  // nostril base (an L of shadow).
  const ns = shade(f.skin, 0.74), s = f.shadeSide;
  vLine(g, cx + (s > 0 ? 1 : -1), noseY, 4, ns);
  g.a(0.6, () => vLine(g, cx - (s > 0 ? 1 : -1), noseY - 1, 4, shade(f.skin, 1.1))); // bridge highlight
  g.r(cx - 1, noseY + 4, 3, 1, ns);
  g.r(cx + (s > 0 ? 2 : -2), noseY + 3, 1, 1, ns);

  // Mouth: a shaded upper-lip line; endpoints lift for a smile, drop for a frown.
  // Females get fuller, subtly rosy lips with a sheen.
  const mw = f.mouthW, x0 = cx - (mw >> 1);
  const open = anim?.mouthOpen || 0;
  const mc = f.female ? lerpHex(shade(f.skin, 0.55), "#9c4f57", 0.55) : shade(f.skin, 0.5);
  const lipCol = f.female ? lerpHex(shade(f.skin, 0.95), "#c07a80", 0.45) : shade(f.skin, 1.06);
  g.r(x0, my, mw, 1, mc); // upper lip line
  if (f.female) {
    g.r(x0, my + 1, mw, 1, lipCol); // lower lip
    g.a(0.6, () => g.r(x0 + 1, my + 1, mw - 2, 1, lerpHex(lipCol, "#ffffff", 0.3))); // sheen
  } else {
    g.a(0.5, () => g.r(x0, my + 1, mw, 1, lipCol));
  }
  // Idle parting: a dark gap opens BETWEEN the lips. Its centre widens
  // symmetrically as `open` (0..1) grows (a pixel added each side at once), and
  // the middle of the lower lip drops one row so the gap sits between the two
  // lips instead of over the bottom lip. open === 0 is the static look.
  if (open > 0) {
    const h = Math.round(open * ((mw - 2) >> 1)), w = 2 * h + 1, gx = cx - h;
    g.r(gx, my + 2, w, 1, f.female ? lipCol : shade(f.skin, 0.9)); // dropped lower-lip centre
    g.r(gx, my + 1, w, 1, shade(f.skin, 0.3)); // dark cavity where the lips part
  }
  // Expression: use the animated curve when present (it morphs between the
  // character's expression and neutral), else the static one. Either way it
  // flattens toward neutral as the mouth opens.
  const curve = Math.round((anim?.mouthCurve ?? f.mouthCurve) * (1 - open));
  if (curve !== 0) {
    g.r(x0 - 1, my - curve, 1, 1, mc);
    g.r(cx + (mw >> 1), my - curve, 1, 1, mc);
  }
}

function drawFacialHair(g, geom, f) {
  if (!f.facialHair) return;
  const { cx, faceHalf, chinY, mouthY: my, maxHalf } = geom;
  const col = shade(f.hairColor, 0.9), dark = shade(f.hairColor, 0.6), lit = shade(f.hairColor, 1.16);
  // Jaw-hair top edge curves up the cheeks (sideburns) instead of a straight line.
  const bTop = (x) => (my - 2) - Math.round(7 * (Math.abs(x - cx) / maxHalf) ** 2);
  switch (f.facialHair) {
    case "stubble":
      // Stippled shadow over the jaw, with the same curved cheek top as the beard.
      g.a(0.34, () => {
        for (let y = my - 9; y <= chinY; y++) {
          const h = Math.round(faceHalf(y));
          if (h <= 1) continue;
          for (let x = cx - h; x < cx + h; x++) {
            if (y < bTop(x) || hash2(x * 2.1, y * 1.3) < 0.45) continue; // curved top + sparse stipple
            g.r(x, y, 1, 1, col);
          }
        }
      });
      break;
    case "mustache":
      g.r(cx - (f.mouthW >> 1) - 1, my - 2, f.mouthW + 2, 2, col);
      break;
    case "beard": {
      // Full beard, textured with lighter/darker strands, a lips gap, and a darker
      // jaw-underline shadow. Top edge curves up the cheeks (shared bTop).
      for (let y = my - 9; y <= chinY + 2; y++) {
        const h = Math.round(faceHalf(Math.min(y, chinY)));
        if (h <= 1) continue;
        for (let x = cx - h; x < cx + h; x++) {
          if (y < bTop(x)) continue; // curved top edge following the cheeks
          if (y >= my - 1 && y <= my + 1 && Math.abs(x - cx) <= (f.mouthW >> 1)) continue; // lips show through
          const n = hash2(x * 1.8, y * 1.5);
          g.r(x, y, 1, 1, n > 0.8 ? dark : n < 0.13 ? lit : col);
        }
      }
      g.a(0.4, () => { const h = Math.round(faceHalf(chinY)); g.r(cx - h + 1, chinY, 2 * h - 2, 2, dark); }); // jaw shadow
      for (let x = cx - (f.mouthW >> 1) - 1; x <= cx + (f.mouthW >> 1); x++) {
        g.r(x, my - 2, 1, 2, hash2(x * 1.8, (my - 2) * 1.5) > 0.8 ? dark : col); // moustache
      }
      break;
    }
    case "goatee":
      g.r(cx - 3, my + 3, 6, 6, col);
      g.r(cx - (f.mouthW >> 1) - 1, my - 2, f.mouthW + 2, 1, col);
      break;
  }
}

// A single earring at (ex, y) — the lobe attach point. Style: stud / hoop / drop.
function drawEarring(g, ex, y, style, color) {
  if (style === "hoop") {
    g.r(ex, y, 1, 1, color); // top attach
    g.r(ex - 1, y + 1, 1, 1, color);
    g.r(ex + 1, y + 1, 1, 1, color);
    g.r(ex - 1, y + 2, 1, 1, color);
    g.r(ex + 1, y + 2, 1, 1, color);
    g.r(ex - 1, y + 3, 1, 1, color);
    g.r(ex + 1, y + 3, 1, 1, color);
    g.r(ex, y + 4, 1, 1, color); // bottom of the ring
  } else if (style === "drop") {
    g.r(ex, y, 1, 1, color); // stud
    g.r(ex, y + 2, 1, 2, color); // dangling drop
  } else { // stud
    g.r(ex, y, 1, 2, color);
  }
}

// Earrings from the earlobe(s). Drawn before hair so long styles cover them (and
// short styles leave them showing). One ear, or both.
function drawEarrings(g, geom, f) {
  if (!f.earring) return;
  const { cx, eyeY } = geom, y = eyeY + 7;
  for (const sgn of f.earringBoth ? [-1, 1] : [1]) {
    drawEarring(g, cx + sgn * Math.round(geom.faceHalf(y) + 0.5), y, f.earringStyle, f.earringColor);
  }
}

// Facial scar / clan markings / faction sigil. Drawn on the skin BEFORE hair, so
// hair renders over them where they overlap (hairline, temples).
function drawFaceMarks(g, geom, f, pal, faction) {
  const cx = geom.cx, ey = geom.eyeY;
  // Scar: a short diagonal across one cheek/brow.
  if (f.scar) {
    let x = cx + f.scarSide * 8, y = ey - 3;
    for (let i = 0; i < 7; i++) {
      g.r(x, y, 1, 1, shade(f.skin, 0.6));
      x += f.scarSide;
      y += 1;
    }
  }
  // Warpaint / clan markings (wilders skew here via markStyle).
  if (f.markings) {
    const ac = faction === "wilders" ? pal.accent : shade(pal.accent, 0.9);
    if (f.markStyle === 0) {
      g.a(0.7, () => { vLine(g, cx - 6, ey - 1, 6, ac); vLine(g, cx + 6, ey - 1, 6, ac); });
    } else if (f.markStyle === 1) {
      // Cheek dashes (below the eyes).
      g.a(0.7, () => { for (const sgn of [-1, 1]) for (let i = 0; i < 3; i++) g.r(sgn < 0 ? cx - 8 : cx + 5, ey + 3 + i * 2, 3, 1, ac); });
    } else {
      g.a(0.6, () => { g.r(cx - 3, ey - 6, 6, 1, ac); disc(g, cx, ey - 8, 1, ac); });
    }
  }
  // Faction sigil on the forehead (dominion mark reads strongest).
  if (f.sigil) {
    const sy = ey - 7;
    if (faction === "dominion") { g.r(cx - 1, sy, 2, 3, pal.accent); g.r(cx - 2, sy + 1, 4, 1, pal.accent); }
    else if (faction === "veil") g.a(0.8, () => disc(g, cx, sy + 1, 2, pal.glow));
    else { g.r(cx - 2, sy, 1, 3, pal.accent); g.r(cx + 1, sy, 1, 3, pal.accent); }
  }
}

// Eyewear (visor / glasses / eyepatch) — drawn on top of everything.
function drawExtras(g, geom, f, pal, faction) {
  const cx = geom.cx, ey = geom.eyeY;
  const [lx, rx] = [cx - (f.eyeGap >> 1), cx + (f.eyeGap >> 1)];
  // Face mask / visor: a solid band wrapping the eyes that follows the face
  // silhouette (so it covers the sides) with a top edge curving up at the
  // temples, a lit top rim, and a glowing eye slit.
  if (f.visor) {
    const dk = pal.garbDark, rim = shade(pal.garbDark, 1.5), ug = shade(pal.glow, 0.6);
    const W = Math.round(geom.faceHalf(ey + 1)), Wface = Math.round(geom.faceHalf(ey));
    const browSpan = (f.eyeGap >> 1) + 3; // glow only reaches as wide as the eyebrows
    // The whole band curves up at the edges — top, slit and bottom all rise by the
    // same offset, so the visor keeps a constant thickness and hugs the face.
    for (let x = cx - W; x <= cx + W; x++) {
      if (Math.abs(x - cx) > Wface) continue;
      const t = (x - cx) / W, off = Math.round(3 * t * t);
      const top = ey - 1 - off, bot = ey + 3 - off, slit = ey - off;
      const lit = Math.abs(x - cx) <= browSpan; // glow limited to the eyebrow span
      for (let y = top; y <= bot; y++) g.r(x, y, 1, 1, lit && y === slit ? pal.glow : y === top ? rim : dk);
      if (lit) g.a(0.45, () => g.r(x, slit + 1, 1, 1, ug)); // under-slit glow, curved too
    }
  } else if (f.glasses) {
    // Eyewear frame around each eye, a bridge, and arms back to the ears. Style is
    // clear (transparent), sun (dark tinted), or aviator (teardrop metal frame).
    const aviator = f.glassesStyle === "aviator";
    const fr = aviator ? "#8a7452" : "#26221e";
    const er = Math.round(geom.faceHalf(ey));
    // Aviator lens: half-widths top->bottom (dy -3..+4). Big teardrop widest near
    // the top, tapering to a rounded bottom point that leans slightly outward.
    const AV = [5,6,6,6,5,5,4,3];
    const avC = (ex, dy) => ex + Math.round((ex < cx ? -1 : 1) * Math.max(0, dy) * 0.28);
    const lens = (ex) => {
      if (aviator) {
        for (let i = 0; i < AV.length; i++) {
          const dy = -3 + i, w = AV[i], c = avC(ex, dy);
          const tint = lerpHex("#2a1e12", "#5c4020", i / (AV.length - 1)); // amber gradient, darker up top
          g.a(0.82, () => g.r(c - w, ey + dy, 2 * w, 1, tint));
          g.r(c - w, ey + dy, 1, 1, fr);
          g.r(c + w - 1, ey + dy, 1, 1, fr);
        }
        hLine(g, ex - AV[0], ey - 3, AV[0] * 2, fr); // flat top
        const bw = AV[AV.length - 1], bc = avC(ex, 4);
        g.r(bc - bw, ey + 5, bw * 2, 1, fr); // rounded bottom rim
        g.a(0.5, () => g.r(ex - 3, ey - 2, 5, 1, "#c9d6e2")); // reflection streak
      } else {
        hLine(g, ex - 4, ey - 3, 9, fr); // rectangular frame
        hLine(g, ex - 4, ey + 3, 9, fr);
        vLine(g, ex - 4, ey - 3, 7, fr);
        vLine(g, ex + 4, ey - 3, 7, fr);
        if (f.glassesStyle === "sun") {
          g.a(0.82, () => g.r(ex - 3, ey - 2, 7, 5, "#161b24")); // dark tint
          g.a(0.5, () => { for (let k = 0; k < 4; k++) g.r(ex - 3 + k, ey + 1 - k, 1, 1, "#5a6b82"); }); // reflection streak
        } else {
          g.a(0.2, () => g.r(ex - 3, ey - 2, 7, 5, "#bcd0e0")); // faint clear tint
          g.r(ex - 2, ey - 2, 1, 1, "#f2f6fa"); // glint
        }
      }
    };
    lens(lx);
    lens(rx);
    if (aviator) {
      const bx = lx + AV[0], bw = Math.max(2, rx - lx - AV[0] * 2);
      hLine(g, bx, ey - 2, bw, fr); // double bridge over the nose
      hLine(g, bx, ey, bw, fr);
      hLine(g, cx - er, ey - 3, lx - AV[1] - (cx - er), fr); // arms at the brow line
      hLine(g, rx + AV[1], ey - 3, cx + er - (rx + AV[1]), fr);
    } else {
      hLine(g, lx + 4, ey - 1, Math.max(2, rx - lx - 8), fr); // single bridge
      hLine(g, cx - er, ey - 2, lx - 4 - (cx - er), fr); // arms to the ears
      hLine(g, rx + 4, ey - 2, cx + er - (rx + 4), fr);
    }
  } else if (f.eyepatch) {
    const ex = cx + (f.scarSide < 0 ? -(f.eyeGap >> 1) : (f.eyeGap >> 1));
    g.r(ex - 2, ey - 2, 6, 6, "#303030");
    g.r(ex - 3, ey - 2, 8, 5, "#303030");
    
    g.r(ex + 3, ey - 2, 1, 5, "#202020");
    g.r(ex - 3, ey + 2, 7, 1, "#202020");

    g.r(ex + 4, ey - 2, 1, 5, "#000000");
    g.r(ex - 2, ey + 3, 6, 1, "#000000");
    hLine(g, cx - geom.maxHalf, ey - 3, geom.maxHalf * 2, "#161418"); // strap
  }
}

// ---- entry points ---------------------------------------------------------
// Paint a portrait from an explicit feature object into a 2D context sized
// AVATAR_BASE x AVATAR_BASE. `rng` drives the few remaining cosmetic randoms
// (the wilders fur edge). Works against any object exposing
// fillStyle/fillRect/globalAlpha (a real canvas, or a recording stub in tests).
function paintAvatar(ctx, f, faction, cfg, rng, anim = null) {
  const pal = AVATAR_PALETTES[faction] || AVATAR_PALETTES.dominion;
  const geom = makeGeom(f);
  const g = makeG(ctx);

  // `transparentBackground` skips the faction fill so the portrait composites
  // onto whatever is behind it (e.g. a card's own gradient) instead of drawing
  // its own rectangle. Not a DEFAULT_AVATAR_CONFIG knob — callers opt in.
  if (!cfg.transparentBackground) drawBackground(g, pal, cfg);
  if (f.hood) cowlBack(g, geom, pal);
  // Breathing lifts only the body/shoulders a pixel (the head stays put, so it
  // reads as the chest rising, not the whole portrait jumping). The head is
  // drawn afterward and covers the 1px more of neck the raised collar exposes.
  const breath = anim?.breath | 0;
  if (breath) ctx.translate(0, -breath);
  drawBody(g, geom, rng, f, pal, faction);
  if (breath) ctx.translate(0, breath);
  drawHead(g, geom, f);
  drawEarrings(g, geom, f); // before hair, so long styles cover the ears + earrings
  drawFaceMarks(g, geom, f, pal, faction); // scar/markings/sigil under the hair
  drawHair(g, geom, f, anim);
  if (f.hood) cowlFront(g, geom, pal);
  drawFace(g, geom, f, pal, anim);
  drawFacialHair(g, geom, f);
  drawExtras(g, geom, f, pal, faction);
  return f;
}

// Draw one portrait, deriving every feature from (seed, faction).
export function drawAvatar(ctx, seedStr, faction = "dominion", config = {}) {
  const cfg = { ...DEFAULT_AVATAR_CONFIG, ...config };
  const rng = makePRNG(`avatar|${faction}|${seedStr}`);
  const f = rollFeatures(rng, faction, cfg);
  return paintAvatar(ctx, f, faction, cfg, rng); // rng continues past the roll
}

// The rolled feature object for a seed — a starting point for manual editing.
export function featuresFromSeed(seedStr, faction = "dominion", config = {}) {
  const cfg = { ...DEFAULT_AVATAR_CONFIG, ...config };
  return rollFeatures(makePRNG(`avatar|${faction}|${seedStr}`), faction, cfg);
}

// Draw a portrait from an explicit feature object (avatar creator), bypassing
// the seeded roll entirely.
export function drawAvatarFeatures(ctx, features, faction = "dominion", config = {}, anim = null) {
  const cfg = { ...DEFAULT_AVATAR_CONFIG, ...config };
  const rng = makePRNG(`avatar-manual|${faction}|${features.skin}|${features.hairStyle}`);
  return paintAvatar(ctx, { ...features }, faction, cfg, rng, anim);
}

// Idle-animation driver: maps an elapsed time (ms) + a per-avatar phase seed to
// the small per-frame `anim` deltas paintAvatar understands. Pure, so the same
// (t, phase) always yields the same pose — a portrait animates identically on
// every client without syncing state. Pass `phase` = a hash of the seed so two
// avatars on screen don't blink in lockstep.
export function avatarAnim(tMs, phase = 0, mouthCurve = 0) {
  const t = tMs / 1000 + phase;
  // Blink: shut for ~140ms once per ~4.2s cycle.
  const blinkCycle = t % 4.2;
  const blink = blinkCycle > 4.06 ? 1 : 0;
  // Gaze saccade: hold centre, occasionally flick to a side and back.
  const g = Math.sin(t * 0.37 + phase * 2.3);
  const gaze = g > 0.9 ? 1 : g < -0.9 ? -1 : 0;
  // Glow pulse: continuous, so it reads smoothly even at this resolution.
  const glow = 0.7 + 0.3 * Math.sin(t * 2.1 + phase);
  // Brow flick: a brief raise, rarely.
  const brow = Math.sin(t * 0.23 + phase) > 0.985 ? 1 : 0;
  // Breathing: the shoulders rise a pixel on the slow inhale (body only).
  const breath = Math.sin(t * 0.8) > 0.3 ? 1 : 0;
  // Expression: slowly morph between the character's set expression and neutral
  // (a neutral character drifts toward a smile or frown, picked per-avatar from
  // phase). Runs at the cadence the mouth-open used to — roughly every ~13s.
  const target = mouthCurve !== 0 ? mouthCurve : (Math.sin(phase * 7) >= 0 ? 1 : -1);
  const k = (Math.sin(t * 0.5 + phase * 5) + 1) / 2;
  const animCurve = Math.round(target * k); // -1 / 0 / 1
  // Mouth opening: now ~4x rarer (a slow ~50s cycle whose peak parts the lips for
  // a few seconds); still a continuous 0..1 that drawFace maps to slit width.
  const mo = Math.sin(t * 0.125 + phase * 3);
  const mouthOpen = mo > 0.93 ? (mo - 0.93) / 0.07 : 0;
  // Wind: mostly still, with occasional gusts that flutter the loose hair. A slow
  // envelope gates a faster oscillation, so gusts arrive and fade (~every 50s).
  const gust = Math.max(0, Math.sin(t * 0.13 + phase * 4) - 0.85) / 0.15;
  const wind = gust * Math.sin(t * 2.4 + phase);
  return { blink, gaze, glow, brow, breath, mouthOpen, mouthCurve: animCurve, wind };
}

// Browser/lab-only: nearest-neighbour upscale of a 64px portrait to `size` px.
function upscaledDataURL(drawInto, size) {
  const src = document.createElement("canvas");
  src.width = src.height = AVATAR_BASE;
  drawInto(src.getContext("2d"));
  const out = document.createElement("canvas");
  out.width = out.height = size;
  const octx = out.getContext("2d");
  octx.imageSmoothingEnabled = false;
  octx.drawImage(src, 0, 0, size, size);
  return out.toDataURL("image/png");
}

export function renderAvatarDataURL(seedStr, faction = "dominion", size = AVATAR_SIZE, config = {}) {
  return upscaledDataURL((ctx) => drawAvatar(ctx, seedStr, faction, config), size);
}

export function renderAvatarFeaturesDataURL(features, faction = "dominion", size = AVATAR_SIZE, config = {}) {
  return upscaledDataURL((ctx) => drawAvatarFeatures(ctx, features, faction, config), size);
}
